import React, {
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
} from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import { formatCurrency, formatPriceLabel } from '@/core/utils/currency';
import { RapidStorSettings } from '@/core/utils/rapidstor';

import {
    ANIMATION_DELAY,
    ANIMATION_SPEED,
    optionsCardVariants,
} from '@/features/rapidstor/animations';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import {
    unitTypeNextSizeUp,
    UpgradeDescriptor,
} from '@/features/rapidstor/UnitOptions';
import type { DisplayedOptions } from '@/journey-based-listing/utils/displayedOptions';

import { useDealSelection, DealOption, NoDealOption } from '@/journey-based-listing/hooks/useDealSelection';
import { useOptionWithDeal, PendingDescriptorSwitch } from '@/journey-based-listing/hooks/useOptionWithDeal';

import classes from './OptionSelector.module.scss';

import Coupon from '../rapidstor/coupon';
import ContactCard from './contact-card';
import OptionsCard from './options-card';
import SelectedCard from './selected-card';
import UpsellCard from './upsell-card';
import { getUnitTypeRate } from './utils/dealMatching';

export interface DealSelectorProps {
    deals: DealOption[];
    noDealOption: NoDealOption;
    onSelectDeal: (dealId: string) => void;
    onClearDeal: () => void;
    location: Location;
}

export interface DescriptorSwitchConfirmProps {
    pendingSwitch: PendingDescriptorSwitch;
    onConfirm: () => void;
    onCancel: () => void;
    location: Location;
}

interface Props {
    descriptor: Descriptor;
    unitType: UnitType;
    location: Location;
    upSell: Descriptor | null;
    deal: string | null;
    /** Resolved once by the screen and shared with the tracking event, so all three agree. */
    displayed: DisplayedOptions;
    desktop?: boolean;
    reservationToken?: string | null;
    makeReservation: () => void;
    loading: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
    selectedID: string;
    setSelectedID: Dispatch<SetStateAction<string>>;
    usingDealSelectorVariant?: boolean;
    dealSelectorReplacesBanner?: boolean;
    DealSelectorComponent?: React.ComponentType<DealSelectorProps>;
    DescriptorSwitchConfirmComponent?: React.ComponentType<DescriptorSwitchConfirmProps>;
}

function OptionSelector({
    descriptor,
    unitType,
    location,
    upSell,
    deal,
    displayed,
    desktop,
    makeReservation,
    reservationToken,
    loading,
    setLoading,
    selectedID,
    setSelectedID,
    usingDealSelectorVariant,
    dealSelectorReplacesBanner,
    DealSelectorComponent,
    DescriptorSwitchConfirmComponent,
}: Props) {
    const {
        unitData,
        selectedDescriptor,
        selectedUnitType,
        setSelectedUnitType,
        reservationData,
    } = useContext(JourneyContext);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const descriptorUpsells: UpgradeDescriptor[] = displayed.descriptorOptions;
    const unitTypeUpsells: UnitType[] = displayed.unitTypeOptions;
    const upSellUnitType = displayed.nextSizeUpUnitType;
    const { hasMultipleOptions } = displayed;

    // Deal selection hooks (only active when usingDealSelectorVariant is enabled)
    const dealSelection = useDealSelection(
        selectedUnitType ?? unitType,
        location,
    );

    const optionWithDeal = useOptionWithDeal(
        descriptor,
        usingDealSelectorVariant ? dealSelection.currentDealId : (deal ?? null),
        unitData?.descriptors ?? [],
        location,
    );

    // Effective deal ID: use hook's value when usingDealSelectorVariant is on, otherwise use prop
    const effectiveDealId = usingDealSelectorVariant ? dealSelection.currentDealId : deal;

    useEffect(() => {
        if (!selectedDescriptor || !selectedUnitType || !reservationData) {
            navigate('/');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDescriptor, selectedID, selectedUnitType]);

    useEffect(() => {
        setSelectedUnitType(unitType);
    }, [descriptor, location, setSelectedUnitType, unitType, upSell]);

    const selectedUnitRate = useMemo(() => {
        if (usingDealSelectorVariant) {
            return dealSelection.getRateForUnitType(selectedUnitType ?? unitType);
        }
        return deal ? unitType.rates.discountedRate : unitType.rates.rate;
    }, [usingDealSelectorVariant, deal, dealSelection, selectedUnitType, unitType]);

    // Use discounted rate only if the upsell has the SAME deal
    // When no matching deal, use stdRate (the true standard rate) rather than
    // rates.rate which comes from the first deal's rate field and may differ
    const upSellDifference = useMemo(() => {
        const effectiveDealIdForUpsell = usingDealSelectorVariant
            ? dealSelection.currentDealId
            : reservationData?.dealObject?._id;
        const upSellRate = getUnitTypeRate(upSellUnitType, effectiveDealIdForUpsell ?? undefined);

        if (selectedUnitRate == null || upSellRate == null) {
            return 0;
        }

        return upSellRate - selectedUnitRate;
    }, [usingDealSelectorVariant, dealSelection.currentDealId, selectedUnitRate, upSellUnitType, reservationData?.dealObject?._id]);

    if (!unitType) {
        return null;
    }

    const dealSelectorElement = usingDealSelectorVariant && DealSelectorComponent && dealSelection.hasDealOptions ? (
        <DealSelectorComponent
            deals={dealSelection.availableDeals}
            noDealOption={dealSelection.noDealOption}
            onSelectDeal={dealSelection.selectDeal}
            onClearDeal={dealSelection.clearDeal}
            location={location}
        />
    ) : null;

    // When the variant replaces the banner, pass it as dealBannerSlot; otherwise as dealSelectorSlot
    const dealBannerSlot = dealSelectorReplacesBanner ? dealSelectorElement : null;
    const dealSelectorSlot = dealSelectorReplacesBanner ? null : dealSelectorElement;

    const descriptorConfirmSlot = usingDealSelectorVariant && DescriptorSwitchConfirmComponent && optionWithDeal.pendingDescriptorSwitch ? (
        <DescriptorSwitchConfirmComponent
            pendingSwitch={optionWithDeal.pendingDescriptorSwitch}
            onConfirm={optionWithDeal.confirmDescriptorSwitch}
            onCancel={optionWithDeal.cancelDescriptorSwitch}
            location={location}
        />
    ) : null;

    // Callback for deal-aware option selection
    const handleOptionSelect = usingDealSelectorVariant
        ? (option: UnitType, optionDescriptor?: UpgradeDescriptor) => {
            if (optionDescriptor) {
                // Descriptor upsell - requires confirmation
                optionWithDeal.handleDescriptorSwitch(optionDescriptor, option);
            } else {
                // Unit-type alternative - immediate switch
                optionWithDeal.handleUnitTypeSwitch(option);
                setSelectedID(option._id);
            }
        }
        : undefined;

    return (
        <div className={`${classes.card_wrapper}`}>
            <SelectedCard
                deal={effectiveDealId ?? null}
                desktop={desktop === true}
                descriptor={descriptor}
                descriptorOptions={descriptorUpsells}
                unitTypeOptions={unitTypeUpsells}
                makeReservation={makeReservation}
                reservationToken={reservationToken ?? null}
                selectedID={selectedID}
                setSelectedID={setSelectedID}
                loading={loading}
                setLoading={setLoading}
                onOptionSelect={handleOptionSelect}
                dealSelectorSlot={dealSelectorSlot}
                dealBannerSlot={dealBannerSlot}
                descriptorConfirmSlot={descriptorConfirmSlot}
            />

            {/* On mobile, there's nothing to divide "or" from unless a bigger-unit
                suggestion is actually available below (otherwise ContactCard is the
                only thing rendered next, and it needs no leading divider). */}
            {(desktop || displayed.nextSizeUp) && (
                upSell
                    && upSellUnitType
                    && unitData
                    && unitTypeNextSizeUp(descriptor.area, unitData.descriptors) ? (
                        <motion.div
                            layout
                            variants={optionsCardVariants}
                            initial="start"
                            animate="show"
                            transition={{
                            duration: ANIMATION_SPEED,
                            delay: ANIMATION_DELAY,
                        }}
                            className={`${classes.upsell_copy} ${!desktop && classes.mobile
                            }`}
                            style={{ margin: desktop ? '15% auto' : '' }}
                        >
                            <span className={`${classes.or}`}>{t('labels:or')}</span>
                            <span className={`${classes.price_difference}`}>
                                {(() => {
                                    if (Math.sign(upSellDifference) === -1) {
                                        return `Upsize and save ${formatPriceLabel(formatCurrency(Math.abs(upSellDifference), location), location)}`;
                                    }
                                    if (Math.sign(upSellDifference) === 0) {
                                        return 'Upsize for no price difference';
                                    }
                                    return `Upsize for an extra ${formatPriceLabel(formatCurrency(upSellDifference, location), location)}`;
                                })()}
                            </span>
                        </motion.div>
                ) : (
                    <motion.div
                        layout
                        variants={optionsCardVariants}
                        initial="start"
                        animate="show"
                        transition={{
                            duration: ANIMATION_SPEED,
                            delay: ANIMATION_DELAY,
                        }}
                        className={`${classes.upsell_copy} ${desktop ? classes.desktop : classes.mobile
                            }`}
                    >
                        <span>{t('labels:or')}</span>
                    </motion.div>
                )
            )}

            {(() => {
                if (displayed.nextSizeUp) {
                    return (
                        <UpsellCard
                            descriptor={displayed.nextSizeUp.descriptor}
                            unitType={displayed.nextSizeUp.unitType}
                            desktop={desktop === true}
                            loading={loading}
                        />
                    );
                }
                if (hasMultipleOptions && desktop === true) {
                    return (
                        <OptionsCard
                            descriptor={descriptor}
                            descriptorOptions={descriptorUpsells}
                            unitTypeOptions={unitTypeUpsells}
                            selectedID={selectedID}
                            setSelectedID={setSelectedID}
                            desktop
                            initialRate={selectedUnitRate}
                            onOptionSelect={handleOptionSelect}
                            descriptorConfirmSlot={descriptorConfirmSlot}
                        />
                    );
                }
                return (
                    <ContactCard
                        location={location}
                        loading={loading}
                        desktop={desktop === true}
                    />
                );
            })()}

            {RapidStorSettings(location).showDealCoupons() &&
                !desktop &&
                !hasMultipleOptions && <Coupon type="base" />}
        </div>
    );
}

export default OptionSelector;
